if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.form = this.querySelector('.search');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = this.querySelector('#product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.dataset.loadingText;
      this.resultsText = this.dataset.resultsText;
      this.requestController = null;
      this.ignoreNextFocus = false;
      this.inputHandler = debounce(this.handleInput.bind(this));
      this.handleFocus = this.handleFocus.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleProductTypeChange = this.handleProductTypeChange.bind(this);
      this.handleReset = this.handleReset.bind(this);
      this.close = this.close.bind(this);
      this.addListeners();
    }

    disconnectedCallback() {
      this.requestController?.abort();
      this.input.removeEventListener('focus', this.handleFocus);
      this.input.removeEventListener('input', this.inputHandler);
      this.form.removeEventListener('submit', this.handleSubmit);
      this.removeEventListener('keydown', this.handleKeydown);
      this.resetBtn.removeEventListener('click', this.handleReset);
      this.overlay.removeEventListener('click', this.close);
      if (this.productTypeSelect) {
        this.productTypeSelect.removeEventListener('change', this.handleProductTypeChange);
      }
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus);
      this.input.addEventListener('input', this.inputHandler);
      this.form.addEventListener('submit', this.handleSubmit);
      this.addEventListener('keydown', this.handleKeydown);
      this.resetBtn.addEventListener('click', this.handleReset);
      this.overlay.addEventListener('click', this.close);
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange);
      }
    }

    getQuery() {
      return this.input.value.trim();
    }

    handleSubmit(event) {
      if (!this.getQuery().length) event.preventDefault();
    }

    handleInput() {
      this.input.setAttribute('value', this.input.value);
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.requestController?.abort();
        this.removeAttribute('results');
        this.results.innerHTML = '';
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    handleProductTypeChange(event) {
      this.productTypeInput.value = event.detail.selectedValue;
      const query = this.getQuery();
      if (query.length) this.getResults(query);
    }

    handleFocus() {
      if (this.ignoreNextFocus) {
        this.ignoreNextFocus = false;
        return;
      }
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;
      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    handleKeydown(event) {
      if (!this.hasAttribute('open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.ignoreNextFocus = true;
        this.close();
        this.input.focus();
        return;
      }

      if (event.target.matches('[role="tab"]')) return;
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const focusableResults = this.getFocusableResults();
      if (!focusableResults.length) return;

      const activeIndex = focusableResults.indexOf(document.activeElement);
      let nextIndex;

      if (event.target === this.input) {
        nextIndex = event.key === 'ArrowDown' ? 0 : focusableResults.length - 1;
      } else if (activeIndex !== -1) {
        nextIndex = event.key === 'ArrowDown'
          ? (activeIndex + 1) % focusableResults.length
          : (activeIndex - 1 + focusableResults.length) % focusableResults.length;
      } else {
        return;
      }

      event.preventDefault();
      focusableResults[nextIndex].focus();
    }

    handleReset() {
      this.requestController?.abort();
      this.input.value = '';
      this.input.setAttribute('value', '');
      this.removeAttribute('results');
      this.results.innerHTML = '';
      this.close();
      this.input.focus();
    }

    getFocusableResults() {
      return Array.from(
        this.results.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')
      ).filter((element) => {
        if (element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
        return element.getClientRects().length > 0;
      });
    }

    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();
      this.requestController?.abort();
      this.requestController = new AbortController();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[type]')}=product`;
      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replaceAll(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(window.RetailerSearch.suggestions(searchTerm, this.productTypeInput?.value), {
          signal: this.requestController.signal
        });
        if (!response.ok) throw new Error(String(response.status));

        const template = document.createElement('template');
        template.innerHTML = await response.text();
        if (searchTerm !== this.getQuery()) return;

        const resultsElement = template.content.querySelector('#shopify-section-retailer-search-suggestions');
        if (!resultsElement) throw new Error('Predictive search response is missing results');

        const resultsMarkup = resultsElement.innerHTML.replaceAll('psearch', this.input.id);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.setAttribute('aria-busy', 'false');
        this.setLiveRegionText('');
        this.close();
      }
    }

    setLiveRegionLoadingState() {
      this.results.setAttribute('aria-busy', 'true');
      this.setLiveRegionText(this.loadingText);
    }

    setLiveRegionText(statusText) {
      this.statusEl.textContent = statusText || '';
    }

    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      window.RetailerSearch.rank(this.results, this.getQuery());
      window.RetailerSearch.format(this.results);
      this.results.setAttribute('aria-busy', 'false');
      this.setAttribute('results', 'true');
      this.open();

      const resultCount = this.results.querySelectorAll('.predictive-search__item').length;
      const noResults = this.results.querySelector('.predictive-search__no-results');
      if (noResults) {
        this.setLiveRegionText(noResults.textContent.trim());
      } else {
        this.setLiveRegionText(this.resultsText.replaceAll('__COUNT__', String(resultCount)));
      }
    }

    open() {
      this.results.hidden = false;
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overlay-predictive-search');
    }

    close() {
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.results.hidden = true;
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.results.setAttribute('aria-busy', 'false');
      document.body.classList.remove('overlay-predictive-search');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
